import {
    isSunday,
    isValid,
    differenceInMinutes,
    format,
    getDay,
    parse,
    parseISO,
    startOfDay,
    startOfMonth,
    compareAsc,
    getISOWeek,
    type Locale,
} from 'date-fns';
import { timeMonday, timeMonth } from '@visx/vendor/d3-time';
import { rollup, group } from '@visx/vendor/d3-array';
import { nb } from 'date-fns/locale';
import { ShiftType } from '@api/ShiftTypeService';

// Standard message for representing an invalid date (new Date("not a date").toString())
export const INVALID_DATE_MSG = 'Invalid Date';

export const isValidDate = (dateToCheck: string | number | Date): boolean => {
    const date = new Date(dateToCheck);
    return !isNaN(date.valueOf());
};

/**
 * Formats a Date or ISO date string as 'dd.MM.yyyy'.
 * Mirrors moment's behavior, including its invalid-date marker.
 */
export const formatDate = (value: Date | string): string => {
    const date = value instanceof Date ? value : parseISO(value);
    return isValid(date) ? format(date, 'dd.MM.yyyy') : INVALID_DATE_MSG;
};

/**
 * Returns the day of the week for a given date. If invalid date
 * is supplied returns -1.
 *
 * @param {Date} date - The date for which to determine the day of the week.
 * @return {number} The day of the week as a number, where Sunday is 6 and Monday is 0.
 */
export const getDayOfWeek = (date: Date): number =>
    isValidDate(date) ? (date.getDay() + 6) % 7 : -1;

/**
 * Calculates the week number inside the month for a given date. If non-Date or
 * Invalid date is supplied returns -1.
 *
 * Possible maximum of week numbers varies from month to month:
 * - 4 (range 0-3) for 28-days fabruary and Monday is on 1st of February
 * - 5 (range 0-4) for most typical months
 * - 6 (range 0-5) for longer months starting on weekend
 *
 * @param {Date} date - The date for which to calculate the week number.
 * @return {number} The week number ranging from 0 to 5.
 */
export const getMonthWeek = (date: Date): number =>
    isValidDate(date) ? timeMonday.count(timeMonth(date), date) : -1;

/**
 * Gets the ISO week number for a given date.
 * ISO weeks start on Monday, week 1 is the first week with at least 4 days in the new year.
 *
 * @param date - Date string or Date object
 * @returns ISO week number (1-53)
 */
export const getISOWeekNumber = (date: string | Date): number => {
    const dateObj = date instanceof Date ? date : new Date(date);
    return getISOWeek(dateObj);
};

/**
 * Returns the number of days in a given month.
 *
 * @param {string} dateString - The date string in the format "YYYY-MM".
 * @return {number} The number of days in the given month.
 */
export const daysInMonth = (dateString: string): number => {
    const [year, month] = dateString.split('-');
    return new Date(parseInt(year ?? ''), parseInt(month ?? ''), 0).getDate();
};

/**
 * Horizontal offset multiplier for month labels in calendar heatmaps.
 * Months starting on Monday get a slightly smaller offset to align the
 * label with the day boxes, mirroring the previous moment-based logic.
 */
export const calculateMonthLabelOffset = (month: string): number =>
    getDay(startOfMonth(parse(month, 'yyyy-MM', new Date()))) === 1 ? 2.5 : 3.0;

/**
 * Function that takes a list of dates as parameter and returns a structure first indexed by year and then month
 * with value as a list of dates (days) in the given month and year
 * @param dates
 * @returns {*}
 */
export const fromDaysToYearsMonthsAndDays = (
    dates: (string | Date)[] | null | undefined,
): Record<number, Record<number, Date[]>> => {
    if (!dates) {
        return {};
    }
    if (!dates.every((d) => isValidDate(d))) {
        return {};
    }
    return dates.reduce((acc, day) => {
        const date = day instanceof Date ? day : parseISO(day);
        const year = date.getFullYear();
        const month = date.getMonth();

        acc[year] ??= {};
        acc[year][month] ??= [];
        acc[year][month].push(date);
        return acc;
    }, {});
};

export const MONTHS_SHORT = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'Mai',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Okt',
    'Nov',
    'Des',
];

interface DateValue {
    Date: string;
    Value: string | number | string[];
}

/**
 * Take list of list of date-based data and group them to month-date based
 * reduced values.
 *
 * Intended input data are in format of Array of objects. Each object has to
 * contain "Date" and one or more "Value" properties.
 *
 * Reduce function supplied has to be able to reduce a list of object with
 * "Date" and one or more "Value" properties (suplied inside the data) into
 * object with signature:
 * ```
 * {
 *      Date: date,
 *      Value: string | number | Array[string]
 * }
 *
 * Return data structure is InternMap with multiple levels, first level is by
 * month (YYYY-MM) format and second level is by date (including days). Each
 * value is the result of the reduce function for the given date.
 *
 * @param {Array[]} data - An array of arrays, where each inner array
 *      represents a dataset.
 * @param {CallableFunction} reduce - Callback function to redice collected date
 *      data into supported types.
 * @return {InternMap} - Nested Map, with each data point grouped by month and
 *      date.
 */
export const rollupAndReduce = <R extends DateValue>(
    data: DateValue[][],
    reduceCall: (el: DateValue[]) => R,
): Map<string, Map<string, R['Value']>> =>
    rollup(
        [...group(data.flat(), (d: DateValue) => d.Date).values()].map((el) =>
            reduceCall(el),
        ),
        (d) => (d[0] as R).Value,
        (d) => d.Date.slice(0, 7),
        (d) => d.Date,
    );

/**
 * Accumulate list of array of days into InternMap grouped by month and date.
 * Accumulation means, that multiple days will be merged into one with value
 * of count.
 *
 * @param {Array} dataArrs - An array of datasets.
 * @return {InternMap} - The accumulated data.
 */
export const accumulateDatasets = (
    dataArrs: [string, string][][],
): Map<string, Map<string, number>> =>
    rollup(
        [...group(dataArrs.flat(), (d) => d[0])].map(([key, value]) => ({
            Date: key,
            Value: new Set(value.map(([, name]) => name).flat()),
        })),
        (d) => d[0]?.Value.size ?? 0,
        (d) => d.Date.slice(0, 7),
        (d) => d.Date,
    );

/**
 * Adjust a function's (onChange) date parameter to be UTC-friendly.
 *
 * Occasionally DatePicker will give us UTC dates, and occasionally
 * it will give locale dates. Convert all dates to UTC to safely
 * convert to ISO string.
 *
 * If supplied parameter is not function, the returned function will
 * just return parameter back.
 *
 * @param {function} onChange - function with date parameter
 * @returns
 */
export const utcOnChange =
    (
        onChange?: ((value: string | undefined) => void) | string,
    ): ((date?: Date) => Date | undefined) =>
    (date) => {
        if (typeof onChange !== 'function') {
            return date;
        }
        if (!date || !(date instanceof Date) || !isValidDate(date)) {
            onChange(undefined);
            return;
        }
        const utc = new Date(
            Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
        );
        onChange(utc.toISOString().split('T')[0]);
    };

const localTimeToHours = (time: string): number => {
    const hours = parseInt(time.split(':')[0] ?? '');
    const minutes = parseInt(time.split(':')[1] ?? '');
    return hours + minutes / 60;
};

/**
 * Converts decimal hours (e.g. 7.5) into an 'HH:mm' string using UTC
 * components to avoid DST shifts. Mirrors moment.utc(...).format('HH:mm'),
 * including the 'Invalid date' marker for non-numeric input.
 */
const hoursToLocalTime = (hours: number): string => {
    const date = new Date(parseFloat(String(hours)) * 3600 * 1000);
    if (!isValidDate(date)) {
        return 'Invalid date';
    }
    const hh = String(date.getUTCHours()).padStart(2, '0');
    const mm = String(date.getUTCMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
};

export const parseTimeStr = (time: string | null | undefined): Date | null => {
    if (!time) {
        return null;
    }
    return parse(time, 'HH:mm', new Date());
};

export const minutesToTimeStr = (
    minutes: number | undefined,
): string | undefined => {
    if (minutes == null) {
        return undefined;
    }

    const totalHours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    const hh = String(totalHours).padStart(2, '0');
    const mm = String(mins).padStart(2, '0');

    return `${hh}:${mm}`;
};

export const timeStrToMinutes = (
    localTime: string | undefined,
): number | undefined => {
    if (!localTime) {
        return undefined;
    }
    const date = parse(localTime, 'HH:mm', new Date());
    return differenceInMinutes(date, startOfDay(date));
};

/**
 * Helper function to get adjusted end time so it does not show more than 24
 * hours when it is on the next day
 * @param {number} end End time in minutes
 */
const getAdjustedEndTimeStr = (end: number): string | undefined => {
    const dayInMinutes = 24 * 60;
    const adjustedEnd = end < dayInMinutes ? end : end - dayInMinutes;

    return minutesToTimeStr(adjustedEnd);
};

/**
 * Helper function for getting the latest end time of a shift type. Based on start and minutesMax
 * @param shiftType ({@link ShiftType})
 */
export const getShiftTypeEndTimeStr = (
    shiftType: Pick<ShiftType, 'start' | 'minutesMax'>,
): string | undefined => {
    const minutes = timeStrToMinutes(shiftType.start);
    if (!minutes) {
        return undefined;
    }
    const end = minutes + shiftType.minutesMax;
    return getAdjustedEndTimeStr(end);
};

/**
 * Helper function for getting the earliest end time of a shift type. Based on coreStart and minutesMin
 * @param shiftType ({@link ShiftType})
 */
export const getShiftTypeCoreEndTimeStr = (
    shiftType: Pick<ShiftType, 'coreStart' | 'minutesMin'>,
): string | undefined => {
    const minutes = timeStrToMinutes(shiftType.coreStart);
    if (!minutes) {
        return undefined;
    }

    const end = minutes + shiftType.minutesMin;
    return getAdjustedEndTimeStr(end);
};

/**
 * Helper function for sorting date strings
 * @param dates
 */
export const sortDateStrings = (dates: string[]): string[] =>
    dates.sort((a, b) =>
        compareAsc(
            parse(a, 'dd/MM/yyyy', new Date()),
            parse(b, 'dd/MM/yyyy', new Date()),
        ),
    );

export const getMinimumMinutesDemandForShiftCode = (
    shiftType: ShiftType,
    demand: number,
): number => (shiftType.minutesMin - shiftType.breakMinutes) * demand;

export const getMaximumMinutesDemandForShiftCode = (
    shiftType: ShiftType,
    demand: number,
): number => (shiftType.minutesMax - shiftType.breakMinutes) * demand;

/**
 * Method for rounding a time string (val) in HH:MM format to number of hours with a minute resolution specified by arg 'res'
 * @param val
 * @param res
 * @returns {string}
 */
const roundTimeToNearestTimeRes = (val: string, res: number): string => {
    const parts = val.split(':');
    let h: string | number = parts[0] ?? '';
    let m = Math.round(parseInt(parts[1] ?? '') / res) * res;

    if (m >= 60) {
        h = parseInt(h) + 1;
        m = 0;
    }
    if (Number(h) >= 24) {
        return '00:00';
    } else if (Number(h) === 23 && m >= 60 - Math.floor(res / 2)) {
        return '00:00';
    }
    return `${h.toString().length < 2 ? `0${h}` : h}:${
        m.toString().length < 2 ? `0${m}` : m
    }`;
};

const norwegianDays = (day: string): string | undefined => {
    if (day === 'MONDAY') {
        return 'Mandag';
    }
    if (day === 'TUESDAY') {
        return 'Tirsdag';
    }
    if (day === 'WEDNESDAY') {
        return 'Onsdag';
    }
    if (day === 'THURSDAY') {
        return 'Torsdag';
    }
    if (day === 'FRIDAY') {
        return 'Fredag';
    }
    if (day === 'SATURDAY') {
        return 'Lørdag';
    }
    if (day === 'SUNDAY') {
        return 'Søndag';
    }
    if (day === 'HOLIDAYS') {
        return 'Helligdager';
    }
};

const addWeeks = (numOfWeeks: number, date = new Date()): Date => {
    date.setDate(date.getDate() + (numOfWeeks * 7 - 1));

    return date;
};

/**
 * The maximum amount of minutes in a single turnus generation
 * @type {number}
 */
const TOKENS_IN_ONE_FULL_GENERATE = 12 * 60;

export const fromGenerationsToGenerateTokens = (
    generations: number | string,
): number => Number(generations) * TOKENS_IN_ONE_FULL_GENERATE;

export const fromGenerateTokensToGenerations = (minutes: number): string =>
    (minutes / TOKENS_IN_ONE_FULL_GENERATE).toFixed(1);

/**
 * Parses a date string in the format "yyyy-MM-dd HH:mm:ss", "yyyy-MM-dd HH:mm", "yyyy-MM-dd", or "yyyy-MM-dd"
 * as a UTC timestamp and returns a Date object representing that moment in time.
 *
 * If the input is not a valid date string in the expected format, the function
 * returns the string "Invalid Date" instead of a Date instance.
 */
export const datetimeStringAsUTCToDate = (
    datetimeString: string,
): Date | typeof INVALID_DATE_MSG => {
    if (!isValidDate(datetimeString)) {
        return INVALID_DATE_MSG;
    }

    const [datePart = '', timePart = '00:00:00'] = datetimeString.split(' ');

    const dateComponents = datePart.split('-').map(Number);
    const [year, month = 1, day = 1] = dateComponents as [
        number,
        number,
        number,
    ];

    if (![year, month, day].every(Number.isFinite)) {
        return INVALID_DATE_MSG;
    }

    const timeComponents = timePart.split(':').map(Number);
    const [hours = 0, minutes = 0, seconds = 0] = timeComponents;

    if (![hours, minutes, seconds].every(Number.isFinite)) {
        return INVALID_DATE_MSG;
    }

    return new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));
};

type FormatParamsObject = {
    date: Date | string | number;
    formatType?: string;
    isUTCString?: boolean;
    locale?: Locale;
};

type FormatParamsTuple = [
    FormatParamsObject['date'],
    FormatParamsObject['formatType']?,
    FormatParamsObject['isUTCString']?,
    FormatParamsObject['locale']?,
];

const isOptionsObject = (
    param: Date | string | number | FormatParamsObject,
): param is FormatParamsObject =>
    !!param && typeof param === 'object' && 'date' in param;

export const formatDateToLocale = (
    ...params: FormatParamsTuple | [options: FormatParamsObject]
): string | typeof INVALID_DATE_MSG => {
    const {
        date,
        formatType = 'P',
        isUTCString = false,
        locale = nb,
    } = params.length === 1 && isOptionsObject(params[0])
        ? params[0]
        : {
              date: params[0] as Date | string | number,
              formatType: params[1],
              isUTCString: params[2],
              locale: params[3],
          };

    if (!isValidDate(date)) {
        return INVALID_DATE_MSG;
    }

    let preparedDate: Date | typeof INVALID_DATE_MSG;

    if (date instanceof Date) {
        preparedDate = date;
    } else if (typeof date === 'string' && isUTCString) {
        preparedDate = datetimeStringAsUTCToDate(date);
    } else {
        preparedDate = new Date(date);
    }

    return preparedDate === INVALID_DATE_MSG
        ? INVALID_DATE_MSG
        : format(preparedDate, formatType, { locale });
};

export const getNextDateString = (dateString: string): string => {
    const date = new Date(dateString);
    date.setUTCDate(date.getUTCDate() + 1);

    return date.toISOString().slice(0, 10);
};

export const checkIsSunday = (date: string | Date): boolean => {
    const formattedDate: Date = date instanceof Date ? date : new Date(date);

    if (!isValid(formattedDate)) {
        return false;
    }

    return isSunday(formattedDate);
};

/**
 * Calculates the actual working time based on the vacancy rate.
 *
 * This represents the effective work time given the
 * current staffing level. A lower vacancy rate reduces the total time.
 *
 * @param fullWorkTime - The total time (100%) worked
 * @param vacancyRate - The staffing level as a percentage (0–100),
 * where e.g. 100 means fully staffed and 50 means half staffed
 * @returns The actual number of working time based on staffing level
 *
 * @throws Error if vacancyRate is less than 0 or greater than 100
 * @throws Error if fullWorkTime is negative
 *
 * @example
 * // 50% staffing reduces 100 to 50 (minutes or hours)
 * getActualWorkTime(10, 50); // 5
 */
export const getActualWorkTime = (
    fullWorkTime: number,
    vacancyRate: number,
): number => {
    if (vacancyRate < 0) {
        throw Error(
            `Vacancy rate must be a non-negative number (${vacancyRate})`,
        );
    }
    if (vacancyRate > 100) {
        throw Error(`Vacancy rate cannot be more than 100 (${vacancyRate})`);
    }

    if (fullWorkTime < 0) {
        throw Error(`Full work time must be non-negative (${fullWorkTime})`);
    }

    return fullWorkTime * (vacancyRate / 100);
};

/**
 * Calculates the vacancy rate (staffing level) based on the
 * full working time and the actual working time.
 *
 * This represents the percentage of staffing available.
 * For example, if 10 hours becomes 5 hours, staffing is 50%.
 *
 * @param fullWorkTime - The total time (100%) worked
 * @param actualWorkTime - The reduced working time based on staffing level
 * @returns The vacancy rate as a percentage (0–100)
 *
 * @throws Error if fullWorkTime is negative
 * @throws Error if actualWorkTime is negative
 *
 * @example
 * // 5 out of 10 hours means 50% staffing
 * getVacancyRate(10, 5); // 50
 */
export const getVacancyRate = (
    fullWorkTime: number,
    actualWorkTime: number,
): number => {
    if (fullWorkTime < 0) {
        throw Error(`Full work time must be non-negative (${fullWorkTime})`);
    }

    if (actualWorkTime < 0) {
        throw Error(
            `Actual work time must be non-negative (${actualWorkTime})`,
        );
    }

    return (actualWorkTime / fullWorkTime) * 100;
};

export const addDaysToIsoDate = (
    isoDate: string,
    days: number,
): string | undefined => {
    const errorMessage = `Expected ISO date. Received ${isoDate}.`;

    const [year, month, day] = isoDate.split('-').map(Number);

    if (!year || !month || !day) {
        console.error(errorMessage);
        return undefined;
    }

    const date = new Date(Date.UTC(year, month - 1, day));

    if (
        date.toISOString().slice(0, 10) !== isoDate ||
        date.getUTCFullYear() !== year ||
        date.getUTCMonth() !== month - 1 ||
        date.getUTCDate() !== day
    ) {
        console.error(errorMessage);
        return undefined;
    }

    date.setUTCDate(date.getUTCDate() + days);

    return date.toISOString().slice(0, 10);
};

export {
    localTimeToHours,
    norwegianDays,
    addWeeks,
    hoursToLocalTime,
    roundTimeToNearestTimeRes,
};
